import {
  Button,
  Chip,
  DropdownItem,
  DropdownMenu,
  DropdownTrigger,
  Input,
  ScrollShadow,
} from '@heroui/react'
import { Dropdown } from '@heroui/react'
import { useActivateReseller, useDeleteReseller, useResellers, useSuspendReseller } from '@qse/api'
import { type Column, ModelControlEnum, UserRoleEnum } from '@qse/constants'
import { useActiveModelStore, useUserStore } from '@qse/stores'
import { ErrorData, LoadingData, TableComponent } from '@qse/ui'
import { useIsMobile, usePageFocusRefetch } from '@qse/utils'
import { ChevronDownIcon } from 'lucide-react'
import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'

const columns: Column[] = [
  { name: 'Reseller', uid: 'user' },
  { name: 'Contact Info', uid: 'contactEmail' },
  { name: 'Tenants', uid: 'tenantCount' },
  { name: 'Usage', uid: 'usage' },
  { name: 'Status', uid: 'status' },
  { name: 'Last Active', uid: 'lastActive' },
  { name: '', uid: 'actions' },
]

const ResellerCard = ({ reseller, onView, onEdit, onDelete, onActivate }: any) => (
  <div className="bg-white dark:bg-[#1A1A1A] rounded-lg border border-gray-200 dark:border-[#2A2A2A] p-4 relative">
    <div className="flex justify-between items-start">
      <div>
        <div className="font-medium text-base">{reseller.name}</div>
        <div className="text-xs text-[#AFAFAF]">ID: {reseller.id}</div>
      </div>
      <Dropdown
        classNames={{
          content: 'bg-white dark:bg-[#343537] border border-gray-200 dark:border-[#707070]',
        }}
      >
        <DropdownTrigger>
          <Button isIconOnly size="sm" variant="light">
            <span className="text-2xl">⋮</span>
          </Button>
        </DropdownTrigger>
        <DropdownMenu>
          <DropdownItem key="view" onPress={() => onView(reseller.id)}>
            View
          </DropdownItem>
          {reseller.status === 'active' ? (
            <DropdownItem key="suspend" onPress={() => onActivate(reseller.id, 'active')}>
              Suspend
            </DropdownItem>
          ) : reseller.status === 'suspended' ? (
            <DropdownItem key="activate" onPress={() => onActivate(reseller.id, 'suspended')}>
              Activate
            </DropdownItem>
          ) : null}
          <DropdownItem key="edit" onPress={() => onEdit(reseller, 'edit')}>
            Edit
          </DropdownItem>
          <DropdownItem key="delete" onPress={() => onDelete(reseller.id)}>
            Delete
          </DropdownItem>
        </DropdownMenu>
      </Dropdown>
    </div>
    <div className="flex justify-between text-sm mt-2">
      <div>
        <div className="font-light">Storage Quota</div>
        <div className="font-medium">{reseller.storageQuota || '4TB'}</div>
      </div>
      <div>
        <div className="font-light">API Quota</div>
        <div className="font-medium">{reseller.apiQuota || '12341234'}</div>
      </div>
    </div>
    <div className="flex justify-between text-xs mt-2">
      <div>
        <div className="font-light">Created Date</div>
        <div>{new Date(reseller.createdAt).toLocaleDateString()}</div>
      </div>
      <Chip
        className="capitalize rounded font-medium"
        size="sm"
        variant="flat"
        style={{
          backgroundColor:
            reseller.status === 'active'
              ? '#378A921A'
              : reseller.status === 'pending'
                ? '#6366F11A'
                : '#F5A5241A',
          color:
            reseller.status === 'active'
              ? '#378A92'
              : reseller.status === 'pending'
                ? '#6366F1'
                : '#F5A524',
        }}
      >
        {reseller.status.charAt(0).toUpperCase() + reseller.status.slice(1)}
      </Chip>
    </div>
  </div>
)

const Reseller = () => {
  const isMobile = useIsMobile()
  const [page, setPage] = useState<number>(1)
  const [rowsPerPage, setRowsPerPage] = useState<number>(10)
  const [search, setSearch] = useState<string>('')
  const { user } = useUserStore()

  const offset = useMemo(() => (page - 1) * rowsPerPage, [page, rowsPerPage])

  const filters = useMemo(
    () => ({
      limit: rowsPerPage,
      offset: offset,
      search: search || undefined,
    }),
    [rowsPerPage, offset, search],
  )

  const isSuperAdmin = user?.userRole === UserRoleEnum.SUPER_ADMIN

  const {
    data: resellersData,
    isLoading,
    error,
    refetch,
  } = useResellers(filters, {
    enabled: isSuperAdmin,
  })

  // Use the page focus refetch hook to automatically refetch data
  usePageFocusRefetch(() => {
    if (isSuperAdmin) {
      refetch()
    }
  })

  const { mutateAsync: mutateAsyncDeleteReseller } = useDeleteReseller()
  const { mutateAsync: mutateAsyncActivateReseller } = useActivateReseller()
  const { mutateAsync: mutateAsyncSuspendReseller } = useSuspendReseller()
  const navigate = useNavigate()

  const handlePageChange = (newPage: number) => {
    setPage(newPage)
  }

  const handleRowsPerPageChange = (newLimit: number) => {
    setRowsPerPage(newLimit)
    setPage(1) // Reset to first page when changing rows per page
  }

  const resellers = resellersData?.data || []
  const pageInfo = resellersData?.pageInfo || { totalPages: 1, count: 0 }

  const totalPages = pageInfo.totalPages || 1

  const handleAddEditReseller = (reseller: any, action: string) => {
    const config = {
      modalHeader: {
        title: action === 'edit' ? 'Edit Reseller' : 'Create New Reseller',
        subtitle:
          action === 'edit'
            ? 'Please fill the form to edit the Reseller.'
            : 'Please fill the form to create a new Reseller.',
        showCloseButton: true,
      },
      data: reseller,
      action: action,
    }

    useActiveModelStore.setState({
      data: config,
      width: 'max-w-[634px]',
      modelActive: ModelControlEnum.ADD_RESELLER,
      toggleModel: true,
      scrollBehavior: 'outside',
    })
  }

  const handleDeleteReseller = (id: string) => {
    const config = {
      modalHeader: {
        title: 'Confirmation',
        showCloseButton: true,
      },

      title: 'Delete Forever?',
      message:
        'This action cannot be undone. Are you sure you want to permanently delete this item?',
      icon: '/assets/svg/delete.svg',
      actions: [
        {
          label: 'Cancel',
          color: 'default',
          variant: 'flat',
        },
        {
          label: 'Delete Forever',
          color: 'danger',
          onPress: () => {
            mutateAsyncDeleteReseller(id)
          },
        },
      ],
    }

    useActiveModelStore.setState({
      data: config,
      width: 'max-w-[660px]',
      modelActive: ModelControlEnum.CONFIRMATION,
      toggleModel: true,
    })
  }

  const handleViewReseller = (id: string) => {
    navigate(`/dashboard/management/tenant/${id}`)
  }

  const handleActivateReseller = (id: string, action: string) => {
    if (action === 'suspended') {
      mutateAsyncActivateReseller(id)
    } else {
      mutateAsyncSuspendReseller(id)
    }
  }

  const renderCell = (data: any, columnKey: Column['uid']) => {
    const reseller = data

    switch (columnKey) {
      case 'user':
        return (
          <div className="flex items-center gap-3 dark:text-white text-black">
            {/* <div className="w-10 h-10 rounded-full bg-[#E15F781A] flex items-center justify-center text-base font-bold uppercase">
              {reseller.name
                ?.split(' ')
                .map((n: string) => n[0])
                .join('')
                .slice(0, 2)}
            </div> */}
            <div className="flex flex-col gap-1 font-light text-sm">
              <p className="text-base font-medium">{reseller.name}</p>
              <p className="text-sm">id: {reseller.id}</p>
            </div>
          </div>
        )
      case 'contactEmail':
        return (
          <div className="dark:text-white text-black">
            <p>{reseller.contactEmail}</p>
            <p>{reseller.phoneNumber}</p>
          </div>
        )

      case 'tenantCount':
        return (
          <span className="dark:text-white text-black font-medium">
            {reseller.tenantCount ?? 0}
            <span className="text-gray-400 font-normal">
              {' / '}
              {reseller.maxTenants != null ? reseller.maxTenants : 'Unlimited'}
            </span>
          </span>
        )
      case 'status':
        return (
          <Chip
            className="capitalize rounded font-medium"
            size="sm"
            variant="flat"
            style={{
              backgroundColor:
                reseller.status === 'active'
                  ? '#378A921A'
                  : reseller.status === 'pending'
                    ? '#6366F11A'
                    : '#F5A5241A',
              color:
                reseller.status === 'active'
                  ? '#378A92'
                  : reseller.status === 'pending'
                    ? '#6366F1'
                    : '#F5A524',
            }}
          >
            {reseller.status.charAt(0).toUpperCase() + reseller.status.slice(1)}
          </Chip>
        )
      case 'lastActive':
        // Since API doesn't provide lastActive, show creation date or placeholder
        return (
          <span className="dark:text-white text-black">
            {new Date(reseller.createdAt).toLocaleDateString()}
          </span>
        )
      case 'actions':
        return (
          <div className="relative flex justify-end items-center gap-2">
            <Dropdown>
              <DropdownTrigger>
                <Button isIconOnly size="sm" variant="light">
                  <span className="text-2xl">⋮</span>
                </Button>
              </DropdownTrigger>
              <DropdownMenu>
                <DropdownItem key="view" onPress={() => handleViewReseller(reseller.id)}>
                  View
                </DropdownItem>
                {reseller.status === 'active' ? (
                  <DropdownItem
                    key="suspend"
                    onPress={() => handleActivateReseller(reseller.id, 'active')}
                  >
                    Suspend
                  </DropdownItem>
                ) : reseller.status === 'suspended' ? (
                  <DropdownItem
                    key="activate"
                    onPress={() => handleActivateReseller(reseller.id, 'suspended')}
                  >
                    Activate
                  </DropdownItem>
                ) : null}
                <DropdownItem key="edit" onPress={() => handleAddEditReseller(reseller, 'edit')}>
                  Edit
                </DropdownItem>
                <DropdownItem key="delete" onPress={() => handleDeleteReseller(reseller.id)}>
                  Delete
                </DropdownItem>
              </DropdownMenu>
            </Dropdown>
          </div>
        )
      default:
        return reseller[columnKey as keyof typeof reseller]
    }
  }

  if (isLoading) {
    return (
      <LoadingData
        description="Please wait while we fetch your data..."
        title="Loading Resellers"
      />
    )
  }
  if (error) {
    return (
      <ErrorData
        description="An unexpected error occurred while loading resellers data."
        title="Error Loading Data"
      />
    )
  }

  return (
    <section className="md:px-[30px] px-4 flex flex-col gap-5">
      <div className=" flex justify-between flex-wrap items-center gap-4">
        <div>
          <h1 className="font-medium text-2xl">Reseller Management</h1>
          <p>Manage your Resellers</p>
        </div>
        <div className="md:flex items-center hidden  gap-3">
          <Input
            isClearable
            placeholder="Find anything"
            radius="sm"
            size="lg"
            startContent={<img alt="search" src="/assets/svg/search.svg" />}
            value={search}
            variant="bordered"
            classNames={{
              label: [
                'text-gray-900',
                'dark:text-foreground',
                'group-data-[filled-within=true]:text-gray-900',
                'dark:group-data-[filled-within=true]:text-white',
                'group-data-[invalid=true]:text-danger',
                'dark:group-data-[invalid=true]:text-danger',
              ],
              input: [
                'bg-transparent min-w-[278px]',
                'text-gray-900',
                'dark:text-white',
                'placeholder:text-gray-500',
                'dark:placeholder:text-white/60',
                'group-data-[invalid=true]:placeholder:text-danger',
              ],
              innerWrapper: 'bg-transparent',
              inputWrapper: [
                'bg-gray-50',
                'dark:bg-[#1e1e20]',
                'border-gray-200',
                'dark:border-default-600',
                'hover:bg-gray-100',
                'dark:hover:bg-[#1e1e20]/70',
                'group-data-[focus=true]:bg-gray-100',
                'dark:group-data-[focus=true]:bg-[#1e1e20]/50',
                'group-data-[focus=true]:border-primary',
                'dark:group-data-[focus=true]:border-primary',
                '!cursor-text',
              ],
            }}
            onClear={() => setSearch('')}
            onValueChange={value => setSearch(value)}
          />
          <Button
            className="md:min-w-[128px] w-full"
            color="primary"
            radius="sm"
            size="lg"
            onPress={() => {
              handleAddEditReseller('', 'create')
            }}
          >
            New Reseller
          </Button>
        </div>
      </div>

      {isMobile ? (
        <div className="">
          {resellers.length === 0 ? (
            <div className="text-center text-gray-600 dark:text-[#AFAFAF] py-8">
              No resellers found.
            </div>
          ) : (
            <ScrollShadow
              hideScrollBar
              className="max-h-[calc(100vh-320px)] overflow-y-auto flex flex-col gap-4"
            >
              {resellers.map(reseller => (
                <ResellerCard
                  key={reseller.id}
                  reseller={reseller}
                  onActivate={handleActivateReseller}
                  onDelete={handleDeleteReseller}
                  onEdit={handleAddEditReseller}
                  onView={handleViewReseller}
                />
              ))}
            </ScrollShadow>
          )}

          <div className="flex justify-between  items-center mt-4 gap-2">
            <Dropdown
              size="sm"
              classNames={{
                content: 'bg-white dark:bg-[#343537] border border-gray-200 dark:border-[#707070]',
              }}
            >
              <DropdownTrigger>
                <Button
                  endContent={<ChevronDownIcon className="text-small" />}
                  size="sm"
                  variant="light"
                >
                  {rowsPerPage}
                </Button>
              </DropdownTrigger>
              <DropdownMenu
                aria-label="Rows per page"
                selectedKeys={new Set([rowsPerPage.toString()])}
                selectionMode="single"
                onAction={key => {
                  setRowsPerPage(Number(key))
                  handleRowsPerPageChange(Number(key))
                }}
              >
                <DropdownItem key="10">10</DropdownItem>
                <DropdownItem key="25">25</DropdownItem>
                <DropdownItem key="100">100</DropdownItem>
              </DropdownMenu>
            </Dropdown>
          </div>
        </div>
      ) : (
        <TableComponent
          columns={columns}
          currentPage={page}
          customRenderCell={renderCell}
          data={resellers}
          disableLocalPagination={true}
          high="h-[calc(100vh-250px)]"
          initialVisibleColumns={[
            'user',
            'contactEmail',
            'tenantCount',
            'status',
            'lastActive',
            'actions',
          ]}
          rowsPerPage={rowsPerPage}
          selectionMode="none"
          showBottomContent={true}
          showTopContent={false}
          total={pageInfo.count || resellers.length}
          totalPages={totalPages}
          trClassName="border-b dark:border-[#2A2A2A] border-[#2A2A2A]/10"
          onLimitChange={handleRowsPerPageChange}
          onPageChange={handlePageChange}
        />
      )}
    </section>
  )
}

export default Reseller
